import { admissionTiming, requestCompanySession, routeSelector } from "../api/companySession";
import {
    isFreshAdmission,
    stageAdmissionHandoff,
    type AdmissionRoute,
    type ReceivedAdmission,
} from "../utils/admissionHandoff";
import { clearOnSignOut } from "../utils/signOutCleanup";

/**
 * A project's admission asked for on intent, before the click: the switcher row the pointer or
 * focus rests on. company-session is called as `TenantScopeProvider`'s `validate` calls it (same
 * transport, the source credential, `routeSelector`), but detached from this tab
 * (`detachedFromTab`): the answer is never installed here and touches none of this tab's
 * credential, renewal, scope, generation, fences or notices. The click hands a fresh answer to
 * the next Document (`utils/admissionHandoff.ts`), which validates it as it would its own.
 */

/** How long the pointer or focus must rest on a row before its admission is asked for. */
export const PREFETCH_INTENT_MS = 150;
/** The most answers kept; another landing drops the oldest. */
const KEPT = 2;
/** An answer this old is asked for again on the next intent, so a click soon after finds it fresh. */
const REFETCH_AFTER_MS = 15_000;
/** A prefetch's whole budget, from when it was sent; a click waits for it no longer than this. */
const PREFETCH_TIMEOUT_MS = 10_000;

const keyOf = (route: AdmissionRoute, source: string) =>
    JSON.stringify([source, route.companySlug, route.projectSlug]);
/** Answers that landed, oldest first. One never used is dropped, never stopped: it is over. */
const kept = new Map<string, ReceivedAdmission>();
interface InFlight {
    key: string;
    controller: AbortController;
    /** Settles, never rejects, once the request is over. */
    landed: Promise<void>;
    /** When its `PREFETCH_TIMEOUT_MS` runs out. */
    deadline: number;
}
/**
 * The one prefetch on its way in this tab. Aborting a request does not cancel it: ai_engine still
 * runs the provider exchange and mints the session. So an intent never abandons this one for
 * another; it waits (`queued`) until this one lands.
 */
let inFlight: InFlight | null = null;
/** The latest intent that came while `inFlight` was on its way, asked for once that lands. */
let queued: { route: AdmissionRoute; source: string } | null = null;

/** Asks for `route`'s admission for the source session `source`, unless one is on its way or
 *  recent; while another is on its way, once that one lands. A failure is dropped: the click
 *  then admits as it would have. */
export function prefetchAdmission(route: AdmissionRoute, source: string): void {
    const key = keyOf(route, source);
    const held = kept.get(key);
    if (inFlight?.key === key || (held && Date.now() - held.receivedAt < REFETCH_AFTER_MS)) {
        // This intent needs no request, and it is the latest: nothing queued is wanted now.
        queued = null;
        return;
    }
    if (inFlight) {
        queued = { route, source };
        return;
    }
    const controller = new AbortController();
    // The next page's first admission is this answer, so it carries the access body.
    const selector = { ...routeSelector(route), include_project_access: true };
    const request: InFlight = {
        key,
        controller,
        landed: Promise.resolve(),
        deadline: Date.now() + PREFETCH_TIMEOUT_MS,
    };
    inFlight = request;
    request.landed = requestCompanySession(selector, {
        timeout: PREFETCH_TIMEOUT_MS,
        retries: 0,
        backoffMs: admissionTiming.initialBackoffMs,
        signal: controller.signal,
        assertCurrent: () => undefined,
        detached: true,
    })
        .then(
            ({ data, deadline, receivedAt }) => {
                if (inFlight !== request) return;
                kept.delete(key);
                kept.set(key, { value: data, deadline, receivedAt });
                while (kept.size > KEPT) kept.delete(kept.keys().next().value as string);
            },
            () => undefined,
        )
        .then(() => {
            if (inFlight !== request) return;
            inFlight = null;
            const next = queued;
            queued = null;
            if (next) prefetchAdmission(next.route, next.source);
        });
}

/** While `route`'s prefetch is on its way: settles when it lands (answered or failed) or its
 *  `PREFETCH_TIMEOUT_MS` runs out, whichever is first. The next page's own request would start
 *  only after a page load, so this one is usually the sooner answer. Null when none is on its
 *  way. */
export function prefetchLanding(route: AdmissionRoute, source: string): Promise<void> | null {
    if (inFlight?.key !== keyOf(route, source)) return null;
    const { landed, deadline } = inFlight;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const expired = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, Math.max(0, deadline - Date.now()));
    });
    return Promise.race([landed, expired]).finally(() => clearTimeout(timer));
}

/** On a click that leaves for `route`: stages its prefetched answer for the next Document, if it
 *  is fresh. A prefetch still on its way is not waited for here (`prefetchLanding`). */
export function handOffPrefetchedAdmission(route: AdmissionRoute, source: string): void {
    const admission = kept.get(keyOf(route, source));
    if (admission && isFreshAdmission(admission))
        stageAdmissionHandoff({ route, source, ...admission });
}

/** Sign-out drops every prefetched answer and the queued intent, and stops the browser waiting on
 *  the one on its way, so its answer never reaches this tab. */
function forgetPrefetchedAdmissions(): void {
    inFlight?.controller.abort();
    inFlight = null;
    queued = null;
    kept.clear();
}
clearOnSignOut(forgetPrefetchedAdmissions);

/**
 * Intent on one switcher row: the pointer or focus resting on it for `PREFETCH_INTENT_MS` runs its
 * action once; a quick pass runs nothing. Pointer and focus count as one rest, because the menu
 * moves focus to the row under the pointer.
 */
export class RowIntent {
    private row: string | null = null;
    private timer: ReturnType<typeof setTimeout> | undefined;

    rest(row: string, act: () => void): void {
        if (this.row === row) return;
        this.cancel();
        this.row = row;
        this.timer = setTimeout(act, PREFETCH_INTENT_MS);
    }

    leave(row: string): void {
        if (this.row === row) this.cancel();
    }

    cancel(): void {
        clearTimeout(this.timer);
        this.timer = undefined;
        this.row = null;
    }
}
